import axios from 'axios';

/**
 * Product Supplier Mapping Service
 * Handles API calls for product-level supplier mappings
 * (option mappings, static values, conditional rules)
 */
export const productMappingService = {
  /**
   * Get the supplier mapping for a product
   * @param {string} productId
   * @param {string} supplierId
   * @returns {Promise<Object|null>} Mapping or null if not found
   */
  async getMapping(productId, supplierId) {
    try {
      const response = await axios.get(
        `/products/${productId}/supplier-mapping`,
        { params: { supplier_id: supplierId } }
      );
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) return null;
      console.error('Error fetching product mapping:', error);
      throw error;
    }
  },

  /**
   * Create or update a product supplier mapping (upsert)
   * @param {string} productId
   * @param {Object} mappingData - { supplier_id, supplier_product_code, option_mappings, static_values, conditional_rules, catalog_id }
   * @returns {Promise<Object>} Created/updated mapping
   */
  async saveMapping(productId, mappingData) {
    try {
      const response = await axios.put(
        `/products/${productId}/supplier-mapping`,
        mappingData
      );
      return response.data;
    } catch (error) {
      console.error('Error saving product mapping:', error);
      throw error;
    }
  },

  /**
   * Delete a product supplier mapping
   * @param {string} productId
   * @param {string} supplierId
   * @returns {Promise<void>}
   */
  async deleteMapping(productId, supplierId) {
    try {
      await axios.delete(
        `/products/${productId}/supplier-mapping`,
        { params: { supplier_id: supplierId } }
      );
    } catch (error) {
      console.error('Error deleting product mapping:', error);
      throw error;
    }
  },

  /**
   * Preview the mapping output with test data
   * @param {string} productId
   * @param {Object} previewData - { enriched_snapshot, format? }
   * @returns {Promise<Object>} { preview, warnings, resolved_values }
   */
  async previewMapping(productId, previewData) {
    try {
      const response = await axios.post(
        `/products/${productId}/supplier-mapping/preview`,
        previewData
      );
      return response.data;
    } catch (error) {
      console.error('Error previewing mapping:', error);
      throw error;
    }
  },

  /**
   * Validate a mapping against an enriched snapshot
   * @param {string} productId
   * @param {Object} validateData - { enriched_snapshot }
   * @returns {Promise<Object>} { valid, errors, warnings }
   */
  async validateMapping(productId, validateData) {
    try {
      const response = await axios.post(
        `/products/${productId}/supplier-mapping/validate`,
        validateData
      );
      return response.data;
    } catch (error) {
      console.error('Error validating mapping:', error);
      throw error;
    }
  },
};

export default productMappingService;
